import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, Banknote, Minus } from 'lucide-react';

export const ExpenseModal = ({ isOpen, onClose, onAddExpense }) => {
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    amount: '',
    payment_method: 'cash',
    category: 'expense'
  });

  const categories = {
    refund: '↩️ Remboursement client',
    supplier: '📦 Paiement fournisseur',
    expense: '💰 Frais divers',
    salary: '👤 Salaire / Avance',
    other: '📝 Autre'
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onAddExpense(expenseForm);
    setExpenseForm({
      description: '',
      amount: '',
      payment_method: 'cash',
      category: 'expense'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-gray-800 border-gray-600 text-white" data-testid="expense-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
            <Minus className="w-5 h-5 text-red-400" />
            Ajouter une sortie de caisse
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="expense-description" className="text-white">Description</Label>
            <Input
              id="expense-description"
              data-testid="expense-description-input"
              value={expenseForm.description}
              onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
              placeholder="Ex: Remboursement écran cassé"
              required
              className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 h-12 text-lg"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="expense-amount" className="text-white">Montant (€)</Label>
            <Input
              id="expense-amount"
              data-testid="expense-amount-input"
              type="number"
              step="0.01"
              value={expenseForm.amount}
              onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
              placeholder="0.00"
              required
              className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 h-12 text-lg font-mono"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white">Catégorie</Label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(categories).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setExpenseForm({ ...expenseForm, category: key })}
                  className={`px-3 py-3 rounded-lg text-sm font-medium transition-all text-left ${
                    expenseForm.category === key
                      ? 'bg-red-600 text-white border-2 border-red-400'
                      : 'bg-gray-700 text-gray-200 border-2 border-transparent hover:bg-gray-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-white">Mode de paiement</Label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="expense-payment-cash-button"
                onClick={() => setExpenseForm({ ...expenseForm, payment_method: 'cash' })}
                className={`flex-1 px-4 py-3 rounded-lg text-sm font-bold transition-all ${
                  expenseForm.payment_method === 'cash'
                    ? 'bg-emerald-500 text-white border-2 border-emerald-300'
                    : 'bg-gray-700 text-gray-200 border-2 border-transparent hover:bg-gray-600'
                }`}
              >
                <Banknote className="w-5 h-5 inline mr-2" />
                Espèce
              </button>
              <button
                type="button"
                data-testid="expense-payment-card-button"
                onClick={() => setExpenseForm({ ...expenseForm, payment_method: 'card' })}
                className={`flex-1 px-4 py-3 rounded-lg text-sm font-bold transition-all ${
                  expenseForm.payment_method === 'card'
                    ? 'bg-sky-500 text-white border-2 border-sky-300'
                    : 'bg-gray-700 text-gray-200 border-2 border-transparent hover:bg-gray-600'
                }`}
              >
                <CreditCard className="w-5 h-5 inline mr-2" />
                CB
              </button>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-gray-600 text-white hover:bg-gray-700"
              data-testid="expense-cancel-button"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
              data-testid="expense-submit-button"
            >
              Ajouter la sortie
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExpenseModal;
